<script setup lang="ts">
import { computed, ref } from "vue";
import { UiChip, UiIcon, UiInputField, UiText } from "@shopexperts/shared";
import type { ExpertPlan } from "@shopexperts/legacy-api";
import {
  describeExpertPlanType,
  formatExpertPlanType,
} from "@/app/composables/expert-plans/expertPlanForm";

const props = defineProps<{
  plans: ExpertPlan[];
  selectedPlanId: number | null;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: "select", plan: ExpertPlan): void;
}>();

const search = ref("");

const filteredPlans = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.plans;
  return props.plans.filter(
    (plan) => plan.name.toLowerCase().includes(query)
      || plan.slug.toLowerCase().includes(query)
      || formatExpertPlanType(plan.expert_type).toLowerCase().includes(query),
  );
});

function pricingSummary(plan: ExpertPlan): string {
  const options = plan.billing_options ?? [];
  if (options.length === 0) return "No pricing";
  const prices = options.length === 1 ? "1 price" : `${options.length} prices`;
  if (!plan.expert_type) return `${prices} · plan not sellable`;
  const live = options.filter((option) => option.is_active).length;
  return live > 0 ? `${prices} · ${live} sellable` : `${prices} · none sellable`;
}
</script>

<template>
  <nav class="flex flex-col gap-3" aria-label="Expert plans">
    <UiInputField
      v-model="search"
      name="plan-search"
      icon="search"
      placeholder="Find a plan..."
    />

    <div v-if="isLoading" class="py-6 text-center">
      <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading plans..." />
    </div>

    <div v-else-if="filteredPlans.length === 0" class="py-6 text-center">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-500"
        :text="search ? 'No plans match your search.' : 'No plans yet.'"
      />
    </div>

    <ul v-else class="flex flex-col gap-1">
      <li v-for="plan in filteredPlans" :key="plan.id">
        <button
          type="button"
          class="w-full text-left px-3 py-2.5 rounded-md cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-se-primary"
          :class="plan.id === selectedPlanId ? 'bg-se-gray-100' : 'hover:bg-se-gray-100/60'"
          :aria-current="plan.id === selectedPlanId ? 'true' : undefined"
          @click="emit('select', plan)"
        >
          <span class="flex items-center gap-2 min-w-0">
            <UiText as="span" variant="p-small" class="font-semibold text-se-dark truncate">
              {{ plan.name }}
            </UiText>
            <UiIcon v-if="plan.is_founding" name="star" class="w-3.5 h-3.5 text-se-primary shrink-0" />
            <UiChip
              v-if="!plan.is_active"
              text="Inactive"
              variant="neutral"
              size="xs"
              tooltip="Plan is inactive — not offered to new experts"
            />
          </span>
          <span class="flex items-center justify-between gap-2 min-w-0">
            <UiText as="span" variant="caption-normal" class="text-se-gray-500 truncate">
              {{ pricingSummary(plan) }}
            </UiText>
            <UiChip
              :text="formatExpertPlanType(plan.expert_type)"
              :variant="plan.expert_type ? 'outline' : 'warn'"
              size="xs"
              class="shrink-0"
              :tooltip="describeExpertPlanType(plan.expert_type)"
            />
          </span>
        </button>
      </li>
    </ul>
  </nav>
</template>
